import { API_BASE_URL } from '@/config/api';

export type BannerMediaType = 'image' | 'video';
export type BannerPlacement = 'home' | 'category';

export interface Banner {
  bannerId: string;
  mediaType: BannerMediaType;
  mediaUrl: string;
  mobileMediaUrl?: string;
  placement: BannerPlacement;
  category?: string;
  redirectUrl?: string;
  buttonText?: string;
  isActive: boolean;
  displayOrder?: number;
  created?: number;
  updated?: number;
}

type RawBanner = Partial<Banner> & Pick<Banner, 'bannerId' | 'mediaUrl'>;

interface BannerListResponse {
  list: RawBanner[];
  areMoreItemsAvailable?: boolean;
}

const normalizeUrl = (url: string | undefined): string => {
  if (!url) return '';
  try {
    return encodeURI(decodeURI(url.trim()));
  } catch {
    return encodeURI(url.trim());
  }
};

const mapBanner = (b: RawBanner): Banner => ({
  bannerId: b.bannerId,
  mediaType: (b.mediaType === 'video' ? 'video' : 'image'),
  mediaUrl: normalizeUrl(b.mediaUrl),
  mobileMediaUrl: normalizeUrl(b.mobileMediaUrl),
  placement: b.placement === 'category' ? 'category' : 'home',
  category: typeof b.category === 'string' ? b.category : '',
  redirectUrl: b.redirectUrl || '',
  buttonText: b.buttonText || '',
  isActive: b.isActive !== undefined ? !!b.isActive : true,
  displayOrder: typeof b.displayOrder === 'number' ? b.displayOrder : 0,
  created: b.created,
  updated: b.updated,
});

export const fetchActiveBanners = async (): Promise<Banner[]> => {
  const res = await fetch(`${API_BASE_URL}/read/v1/service/banners`);
  if (!res.ok) return [];
  const data: BannerListResponse = await res.json();
  return (data.list || []).map(mapBanner);
};

export const fetchAdminBanners = async (token: string): Promise<Banner[]> => {
  const res = await fetch(`${API_BASE_URL}/api/banner/fetch`, {
    headers: { token },
  });
  if (!res.ok) throw new Error('Failed to fetch banners');
  const data: BannerListResponse = await res.json();
  return (data.list || []).map(mapBanner);
};
